import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { deliveryClient } from '../../../lib/connect/clients';
import { pastPerformanceClient } from '../../Library/ppClient';
import { Choice, options, TextField } from './Fields';
export function CreateReference({ onCreated }) {
    const [form, setForm] = useState({
        title: '',
        origin: 'partner',
        provider: '',
        basis: 'partner_reply',
        role: '',
        summary: '',
        customer: '',
        scope: '',
        capabilities: '',
        limitations: '',
        source: '',
        contract: '',
        outcome: '',
        contact: ''
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const companies = useQuery({
        queryKey: [
            'pp-provider-companies'
        ],
        queryFn: async ()=>(await deliveryClient.listCompanyProfiles({})).profiles
    });
    const field = (key, label, testId)=>/*#__PURE__*/ _jsx(TextField, {
            label: label,
            value: form[key],
            testId: testId,
            onChange: (value)=>setForm({
                    ...form,
                    [key]: value
                })
        }, key);
    return /*#__PURE__*/ _jsxs("details", {
        className: "rounded-md border p-3",
        children: [
            /*#__PURE__*/ _jsx("summary", {
                className: "cursor-pointer font-medium",
                children: "Add manual reference — no Program required"
            }),
            /*#__PURE__*/ _jsxs("form", {
                className: "mt-3 grid gap-3 sm:grid-cols-2",
                onSubmit: async (event)=>{
                    event.preventDefault();
                    setBusy(true);
                    setError('');
                    try {
                        const result = await pastPerformanceClient.createPastPerformanceReference({
                            title: form.title,
                            summary: form.summary,
                            customerAgencyName: form.customer,
                            providerCompanyProfileId: form.origin === 'partner' ? form.provider : '',
                            scopeContext: form.scope,
                            capabilityContext: form.capabilities,
                            preparedContentFormat: 'plain_text',
                            details: {
                                referenceOrigin: form.origin,
                                providerRole: form.role,
                                basisKind: form.basis,
                                limitations: form.limitations,
                                sourcePerson: form.source,
                                contractIdentifier: form.contract,
                                outcome: form.outcome,
                                referenceContact: form.contact
                            }
                        });
                        if (!result.reference?.item) throw new Error('Reference was not returned. Retry loading references before creating again.');
                        onCreated(result.reference);
                    } catch  {
                        setError('Reference could not be created. Your entries are retained; check your access and retry.');
                    } finally{
                        setBusy(false);
                    }
                },
                children: [
                    field('title', 'Reference title', Testids.pursuitPP.createTitle),
                    /*#__PURE__*/ _jsx(Choice, {
                        label: "Origin",
                        value: form.origin,
                        options: options([
                            'partner',
                            'historical',
                            'internal'
                        ]),
                        testId: Testids.pursuitPP.origin,
                        onChange: (origin)=>setForm({
                                ...form,
                                origin
                            })
                    }),
                    form.origin === 'partner' && /*#__PURE__*/ _jsx(Choice, {
                        label: "Provider company (existing identity)",
                        value: form.provider,
                        testId: Testids.pursuitPP.provider,
                        options: [
                            {
                                value: '',
                                label: companies.isLoading ? 'Loading companies…' : 'Choose permitted company'
                            },
                            ...(companies.data ?? []).map((c)=>({
                                    value: c.id,
                                    label: c.name
                                }))
                        ],
                        onChange: (provider)=>setForm({
                                ...form,
                                provider
                            })
                    }),
                    companies.isError && /*#__PURE__*/ _jsxs("p", {
                        role: "alert",
                        children: [
                            "Company identities unavailable.",
                            ' ',
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                variant: "secondary",
                                onClick: ()=>void companies.refetch(),
                                children: "Retry companies"
                            })
                        ]
                    }),
                    form.origin === 'partner' && companies.data?.length === 0 && /*#__PURE__*/ _jsx("p", {
                        children: "No permitted companies. Ask a registry owner to record the provider identity, then retry companies."
                    }),
                    /*#__PURE__*/ _jsx(Choice, {
                        label: "Evidence basis",
                        value: form.basis,
                        testId: Testids.pursuitPP.basis,
                        options: options([
                            'partner_reply',
                            'customer_confirmation',
                            'archived_record',
                            'delivery_interview',
                            'manual_note'
                        ]),
                        onChange: (basis)=>setForm({
                                ...form,
                                basis
                            })
                    }),
                    field('role', 'Provider role'),
                    field('customer', 'Customer (if known)'),
                    field('scope', 'Reference scope'),
                    field('capabilities', 'Reference capabilities'),
                    field('summary', 'Source summary'),
                    field('limitations', 'Source limitations'),
                    field('source', 'Source person / call / email / upload reference / old proposal citation'),
                    field('contract', 'Contract identifier (optional)'),
                    field('outcome', 'Recorded outcome (if known)'),
                    field('contact', 'Reference contact (optional)'),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm",
                        children: "Incomplete references are allowed. Manual evidence is unverified; creation grants no Program access."
                    }),
                    error && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: error
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "submit",
                        "data-testid": Testids.pursuitPP.create,
                        disabled: busy || !form.title.trim() || form.origin === 'partner' && !form.provider,
                        children: busy ? 'Creating reference…' : 'Create reference'
                    })
                ]
            })
        ]
    });
}
